import { isDrawerPanelView } from '@/shell/drawerPanel';
import { useEffect, useRef, useState } from 'react';
import { CloudOff, X } from 'lucide-react';
import {
  isMaintenanceStatusExpired,
  parseMaintenanceStatus,
  type IMaintenanceStatus,
} from '@featit/shared/maintenance';
import { MAINTENANCE_POLL_INTERVAL_MS } from '@/buildConstants';
import { BOTTOM_BAR_ROW, ViewportBottomBar } from '@/components/AppViewport';
import { API_BASE_URL } from '@/services/ApiService';
import { DATASET_SYNC_RECOVERY_EVENT } from '@/local-sync/datasetSync';
import { deploymentNeedsChoosing } from '@/features/onboarding/deploymentChoice';
import { SHELL_CONNECTIVITY_EVENT } from '@/shell/shellOffline';
import { MAINTENANCE_STATUS_EVENT } from './maintenanceEvents';
import {
  maintenancePollDelayMs,
  readMaintenancePollIntervalMs,
  type MaintenanceNoticeState,
} from './maintenancePolling';
import { Waiting } from '@/components/Waiting';
import { useT } from '@/i18n/LocaleProvider';

type NoticeState = MaintenanceNoticeState;

const HEALTHY_POLL_INTERVAL_MS = readMaintenancePollIntervalMs(MAINTENANCE_POLL_INTERVAL_MS);

export function MaintenanceNotice() {
  const t = useT();
  const [notice, setNotice] = useState<NoticeState>(null);
  /*
   * Dismissal is keyed on what is being reported, not on a bare boolean.
   *
   * A panel the reader closed must stay closed while the same thing is still wrong, and must come
   * back when something else goes wrong — otherwise closing "Reconnecting" once would also swallow
   * a later "Updating FEATit", which is a different message about a different situation. Storing
   * the dismissed kind rather than `true` gets both without a second piece of state.
   */
  const [dismissedKind, setDismissedKind] = useState<string | null>(null);
  const previousNoticeRef = useRef<NoticeState>(null);

  useEffect(() => {
    /*
     * An installation that has never reached a deployment is not "reconnecting".
     *
     * This notice is for a tenant the reader already has: it promises the current view stays open
     * and that cached data is still there, and neither is true before anybody has connected. On the
     * connect screen it also lands directly above a banner saying the opposite, which is two
     * amber boxes disagreeing about what is wrong. Found by looking at the screen.
     */
    if (deploymentNeedsChoosing()) return undefined;

    let stopped = false;
    let checking = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    /*
     * The next check is paced by what the last one found, and a tab nobody is looking at asks
     * nothing at all (I-0232).
     *
     * Before this the timer was a flat two seconds that scheduled unconditionally, which got two
     * things wrong at once. A healthy tab asked "are you down?" 1,800 times an hour to be told no,
     * and a hidden tab kept the full cadence for as long as it stayed open — it only failed to show
     * up in the request log because Safari freezes background timers, which Chrome does not do to a
     * tab that is merely occluded. `handleVisibility` checks once on the way back, so stopping here
     * misses nothing: the answer is re-asked before anyone can read it.
     */
    const scheduleNext = () => {
      if (timer) clearTimeout(timer);
      timer = undefined;
      if (stopped || document.visibilityState === 'hidden') return;
      timer = setTimeout(
        check,
        maintenancePollDelayMs(previousNoticeRef.current, HEALTHY_POLL_INTERVAL_MS),
      );
    };

    const applyNotice = (next: NoticeState) => {
      if (stopped) return;
      const previous = previousNoticeRef.current;
      previousNoticeRef.current = next;
      setNotice(next);
      if (previous && !next) {
        window.dispatchEvent(new Event(DATASET_SYNC_RECOVERY_EVENT));
      }
    };

    const check = async () => {
      if (stopped || checking) return;
      checking = true;
      try {
        const response = await fetch(`${API_BASE_URL}/maintenance`, {
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (response.status === 503) {
          const status = parseMaintenanceStatus(await response.json().catch(() => null));
          applyNotice(status ? { kind: 'maintenance', status } : { kind: 'offline' });
        } else if (response.status === 404) {
          /*
           * 404 means a backend older than its own route -- not dead code.
           *
           * All three deployed edges answer here themselves (the production router was the last,
           * I-0232). With no edge in front (`yarn dev`, `vite preview` against dev) the request
           * reaches Nest, which answers 204 itself since I-1552 (`MaintenanceController`): before
           * that every tick was a 404 the delivery monitor counted, and dev raised a Critical fault
           * (DM-15). A backend from before it still answers 404, and reading that as healthy keeps
           * such a session out from under a permanent "Reconnecting" banner.
           */
          applyNotice(null);
        } else if (response.ok) {
          applyNotice(null);
        } else {
          applyNotice({ kind: 'offline' });
        }
      } catch {
        applyNotice({ kind: 'offline' });
      } finally {
        checking = false;
        scheduleNext();
      }
    };

    /*
     * A status that arrived in-band re-paces the timer as well as raising the notice.
     *
     * `ApiService` publishes here whenever any request comes back 503 carrying a status, which is
     * how the banner actually appears for anyone using the app. Without the reschedule the notice
     * would go up immediately and then wait out whatever healthy interval was already pending —
     * up to five minutes in production — before anything could take it down again. Raising a
     * notice nothing is due to clear is the dead end this endpoint exists to avoid.
     */
    const handlePublishedStatus: EventListener = (event) => {
      const status = (event as CustomEvent<IMaintenanceStatus | null>).detail;
      applyNotice(status ? { kind: 'maintenance', status } : null);
      scheduleNext();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void check();
      else scheduleNext();
    };

    /*
     * F-0236: the app shell's own `connectivity` signal, which knows there is no network before any
     * request would fail. `online: false` raises the same offline notice `check()`'s catch raises,
     * but without sending the request that notice exists to explain away — asking "are you down?"
     * of a server the device cannot reach is the exact waste this proactive signal avoids. `online:
     * true` re-checks at once rather than waiting out whatever recovery delay was already pending,
     * the same reasoning `handlePublishedStatus` already applies to a status that arrives in-band.
     */
    const handleShellConnectivity: EventListener = (event) => {
      const online = (event as CustomEvent<boolean>).detail;
      if (online) {
        void check();
        return;
      }
      applyNotice({ kind: 'offline' });
      scheduleNext();
    };

    window.addEventListener(MAINTENANCE_STATUS_EVENT, handlePublishedStatus);
    window.addEventListener(SHELL_CONNECTIVITY_EVENT, handleShellConnectivity);
    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', handleVisibility);
    void check();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener(MAINTENANCE_STATUS_EVENT, handlePublishedStatus);
      window.removeEventListener(SHELL_CONNECTIVITY_EVENT, handleShellConnectivity);
      window.removeEventListener('online', check);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // I-1138: not in the More drawer's panel view; the screen behind it carries the notice.
  if (!notice || isDrawerPanelView()) return null;
  if (dismissedKind === noticeIdentity(notice)) return null;
  const expired =
    notice.kind === 'maintenance' && isMaintenanceStatusExpired(notice.status, Date.now());
  const title =
    notice.kind === 'offline'
      ? t('navigation.maintenance.reconnecting')
      : expired
        ? t('navigation.maintenance.takingLonger')
        : t('navigation.maintenance.updating');
  const detail =
    notice.kind === 'maintenance' ? notice.status.message : t('navigation.maintenance.unavailable');

  /*
   * A panel in the layout, not a floating card over it.
   *
   * It used to be `fixed` at the top of the viewport, which put it on top of whatever was
   * underneath — on the desktop shell that is the tab strip and the first row of content. A
   * condition that persists (a deployment being down persists for as long as it is down) should
   * take its own space rather than permanently cover somebody else's, and should be closable,
   * because the reader who has understood it still needs the app.
   *
   * I-2377: and it is a bottom bar, a status strip at the window's bottom edge (on a phone, on the
   * tab bar). As a top bar it pushed the tab strip and the page down for as long as a release
   * installed, and in the Mac app its title ran under the window buttons ("Up⬤ting FEATit"). Jay:
   * "this info should be at the bottom not on top". Its seam is therefore its top border.
   */
  return (
    <ViewportBottomBar order="maintenance">
      <div
        role="status"
        aria-live="polite"
        data-bottom-bar="maintenance"
        className={`flex ${BOTTOM_BAR_ROW} items-start gap-3 border-t border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-950 dark:border-amber-700/60 dark:bg-[#2b2618] dark:text-amber-100`}
      >
        {notice.kind === 'maintenance' ? (
          <Waiting size="inline" className="mt-0.5" />
        ) : (
          <CloudOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1 text-[13px] leading-5">
          <span className="font-semibold">{title}</span>
          <span className="ml-2 text-amber-800 dark:text-amber-200">{detail}</span>
          <span className="ml-2 text-amber-700 dark:text-amber-300">
            {t('navigation.maintenance.cached')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setDismissedKind(noticeIdentity(notice))}
          aria-label={t('navigation.sidebar.dismiss')}
          title={t('navigation.sidebar.dismiss')}
          className="-my-2.5 -mr-3 grid h-touch w-touch shrink-0 place-items-center rounded text-amber-700 sm:my-0 sm:-mr-1 sm:block sm:h-auto sm:w-auto sm:p-1 transition-colors hover:bg-amber-100 hover:text-amber-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-amber-600 dark:text-amber-300 dark:hover:bg-amber-900/40 dark:hover:text-amber-100"
        >
          <X className="h-4 w-4 max-sm:touch-glyph" aria-hidden="true" />
        </button>
      </div>
    </ViewportBottomBar>
  );
}

/**
 * What the panel is currently reporting, as a value a dismissal can be compared against.
 *
 * The message is what the reader dismissed, so two different maintenance windows with different
 * messages are two different things to be told about, and the second is shown even though the
 * first was closed.
 */
function noticeIdentity(notice: Exclude<NoticeState, null>): string {
  return notice.kind === 'maintenance' ? `maintenance:${notice.status.message}` : 'offline';
}
